import axios from 'axios';

const resolveApiUrl = () => {
  const raw = (process.env.REACT_APP_BACKEND_URL || '').trim();
  const isBrowser = typeof window !== 'undefined';
  const origin = isBrowser ? window.location.origin : '';
  const normalized = raw.replace(/\/$/, '');

  // In production behind Nginx, local loopback URLs break client-side requests.
  const isLocalLoopback = /^(https?:\/\/)?(127\.0\.0\.1|localhost)(:\d+)?$/i.test(normalized);
  const base = !normalized || (process.env.NODE_ENV === 'production' && isLocalLoopback)
    ? origin
    : normalized;

  return /\/api$/i.test(base) ? base : `${base}/api`;
};

const API_URL = resolveApiUrl();

export const api = {
  // Tontines
  getTontines: () => axios.get(`${API_URL}/tontines`),
  getTontinesPaginated: (limit = 6, offset = 0, search = '') =>
    axios.get(`${API_URL}/tontines/paginated`, {
      params: { limit, offset, search: search || undefined },
    }),
  getTontine: (id) => axios.get(`${API_URL}/tontines/${id}`),
  createTontine: (data) => axios.post(`${API_URL}/tontines`, data),
  updateTontine: (id, data) => axios.put(`${API_URL}/tontines/${id}`, data),
  deleteTontine: (id) => axios.delete(`${API_URL}/tontines/${id}`),
  joinTontine: (id) => axios.post(`${API_URL}/tontines/${id}/join`),
  joinByInvite: (inviteCode) => axios.post(`${API_URL}/join/${inviteCode}`),
  getTontineMembers: (id) => axios.get(`${API_URL}/tontines/${id}/members`),
  getTontineContributions: (id, params = {}) => axios.get(`${API_URL}/tontines/${id}/contributions`, { params }),

  // Invitations
  createInvite: (tontineId, data = {}) => axios.post(`${API_URL}/tontines/${tontineId}/invite`, data),
  getInvite: (tontineId) => axios.get(`${API_URL}/tontines/${tontineId}/invite`),
  getInviteAudit: (tontineId, limit = 20) => axios.get(`${API_URL}/tontines/${tontineId}/invite/audit`, { params: { limit } }),
  getInviteInfo: (inviteCode) => axios.get(`${API_URL}/invite/${inviteCode}/info`),

  // Member Management
  addMember: (tontineId, data) => axios.post(`${API_URL}/tontines/${tontineId}/add-member`, data),
  
  // Settings
  getTontineSettings: (tontineId) => axios.get(`${API_URL}/tontines/${tontineId}/settings`),
  updateTontineSettings: (tontineId, data) => axios.put(`${API_URL}/tontines/${tontineId}/settings`, data),

  // Contributions
  getContributions: (params = {}) => axios.get(`${API_URL}/contributions`, { params }),
  createContribution: (data) => axios.post(`${API_URL}/contributions`, data),

  // Payments
  initiateStripePayment: (tontineId, originUrl, amount = null) => 
    axios.post(`${API_URL}/payments/initiate`, {
      tontine_id: tontineId,
      origin_url: originUrl,
      ...(amount != null ? { amount } : {}),
    }),
  initiateWavePayment: (tontineId, originUrl, amount = null) => 
    axios.post(`${API_URL}/payments/wave/initiate`, {
      tontine_id: tontineId,
      origin_url: originUrl,
      ...(amount != null ? { amount } : {}),
    }),
  getPaymentStatus: (sessionId) => 
    axios.get(`${API_URL}/payments/status/${sessionId}`),

  // Rotation
  executeRotation: (tontineId) => 
    axios.post(`${API_URL}/tontines/${tontineId}/execute-rotation`),

  // Import/Export
  importMembers: (tontineId, csvContent, mode = 'basic') =>
    axios.post(`${API_URL}/tontines/${tontineId}/import-members`, { csv_content: csvContent, mode }),
  getCsvTemplate: (tontineId, mode = 'basic') =>
    axios.get(`${API_URL}/tontines/${tontineId}/csv-template?mode=${mode}`, { responseType: 'blob' }),
  exportContributionsCsv: (tontineId) =>
    axios.get(`${API_URL}/tontines/${tontineId}/export/csv`, { responseType: 'blob' }),
  exportMembersCsv: (tontineId) =>
    axios.get(`${API_URL}/tontines/${tontineId}/export/members-csv`, { responseType: 'blob' }),
  exportPdf: (tontineId) =>
    axios.get(`${API_URL}/tontines/${tontineId}/export/pdf`, { responseType: 'blob' }),

  // Referrals
  getReferralStats: () => axios.get(`${API_URL}/referrals/stats`),
  getReferralsList: () => axios.get(`${API_URL}/referrals/list`),

  // Notifications (In-App)
  getNotifications: (limit = 50, unreadOnly = false, offset = 0) => 
    axios.get(`${API_URL}/notifications`, { params: { limit, unread_only: unreadOnly, offset } }),
  getNotificationCount: () => axios.get(`${API_URL}/notifications/count`),
  markNotificationRead: (notificationId) => 
    axios.put(`${API_URL}/notifications/${notificationId}/read`),
  markAllNotificationsRead: () => axios.put(`${API_URL}/notifications/read-all`),
  deleteNotification: (notificationId) => 
    axios.delete(`${API_URL}/notifications/${notificationId}`),
  
  // Send Reminders (Admin)
  sendReminders: (tontineId) => 
    axios.post(`${API_URL}/notifications/send-reminders?tontine_id=${tontineId}`),
  sendSmartReminders: (tontineId, highRiskOnly = false) =>
    axios.post(`${API_URL}/notifications/send-smart-reminders?tontine_id=${tontineId}&high_risk_only=${highRiskOnly}`),

  // Risk Scoring
  getMemberRiskScore: (tontineId, memberId, includeAi = true) =>
    axios.get(`${API_URL}/tontines/${tontineId}/members/${memberId}/risk-score`, { params: { include_ai: includeAi } }),
  getTontineRiskSummary: (tontineId) =>
    axios.get(`${API_URL}/tontines/${tontineId}/risk-summary`),

  // Dashboard
  getDashboardStats: () => axios.get(`${API_URL}/dashboard/stats`),

  // Profile
  getMe: () => axios.get(`${API_URL}/auth/me`),
  updateMe: (data) => axios.put(`${API_URL}/auth/me`, data),
  updatePreferences: (data) => axios.put(`${API_URL}/auth/preferences`, data),

  // Admin Dashboard
  getAdminStats: () => axios.get(`${API_URL}/admin/stats`),
  getAdminTontines: () => axios.get(`${API_URL}/admin/tontines`),
  getAdminRiskAlerts: () => axios.get(`${API_URL}/admin/risk-alerts`),
  getAdminTontineHealth: () => axios.get(`${API_URL}/admin/tontine-health`),
  runAdminTontineHealthAction: (data) => axios.post(`${API_URL}/admin/tontine-health/actions`, data),
  getAdminCatchupPlans: (params = {}) => axios.get(`${API_URL}/admin/catchup-plans`, { params }),
  updateAdminCatchupPlanStatus: (planId, data) => axios.put(`${API_URL}/admin/catchup-plans/${planId}/status`, data),
  getMyCatchupPlans: (params = {}) => axios.get(`${API_URL}/catchup-plans/my`, { params }),
  acceptCatchupPlan: (planId) => axios.post(`${API_URL}/catchup-plans/${planId}/accept`),
  exportAdminPendingActionsCsv: (priority = null) =>
    axios.get(`${API_URL}/admin/pending-actions/export/csv`, {
      responseType: 'blob',
      params: priority ? { priority } : undefined,
    }),

  // Wave Payments
  createWaveCheckout: (tontineId) => 
    axios.post(`${API_URL}/payments/wave/checkout?tontine_id=${tontineId}`),
  getWaveStatus: (transactionId) => 
    axios.get(`${API_URL}/payments/wave/status/${transactionId}`),

  // Push Notifications
  subscribePush: (subscription) => 
    axios.post(`${API_URL}/push/subscribe`, subscription),
  unsubscribePush: (endpoint) => 
    axios.delete(`${API_URL}/push/unsubscribe`, { params: { endpoint } }),
  getVapidKey: () => axios.get(`${API_URL}/push/vapid-key`),
  testPush: () => axios.post(`${API_URL}/push/test`),

  // Chat
  sendMessage: (message, sessionId) => 
    axios.post(`${API_URL}/chat`, { message, session_id: sessionId }),
  getChatHistory: (sessionId) => 
    axios.get(`${API_URL}/chat/history`, { params: { session_id: sessionId } }),
};

export const formatCurrency = (amount, currency = 'XOF') => {
  if (currency === 'XOF') {
    return new Intl.NumberFormat('fr-SN', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
    }).format(amount);
  }
  return new Intl.NumberFormat('fr-CA', {
    style: 'currency',
    currency: 'CAD',
  }).format(amount);
};

export const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

export const getFrequencyLabel = (frequency) => {
  const labels = {
    weekly: 'Hebdomadaire',
    biweekly: 'Bimensuel',
    monthly: 'Mensuel',
  };
  return labels[frequency] || frequency;
};

export const getTontineTypeLabel = (type) => {
  const labels = {
    rosca: 'Rotation (ROSCA)',
    asca: 'Épargne (ASCA)',
    flex_asca: 'Épargne libre (ASCA flexible)',
    hybrid: 'Hybride',
  };
  return labels[type] || type;
};
